import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { pollingRefetchInterval } from "../../../utils/requestFailure";
import type { DevBoardMention } from "../types";
import { DEV_BOARD_MENTIONS_READ_EVENT } from "./useDevBoard";

const DEV_BOARD_NOTIFICATIONS_KEY = ["dev-board", "notifications"];
const DEV_BOARD_NOTIFICATIONS_POLL_MS = 60_000;

export interface DevBoardNotifications {
  mentions: DevBoardMention[];
  /** Unread mention and participant rows, for the sidebar badge. */
  count: number;
}

async function fetchDevBoardNotifications(): Promise<DevBoardNotifications> {
  const response = await api.get("/api/v1/dev-board/notifications");
  const count = Number(response.data?.count ?? 0);
  return {
    mentions: (response.data?.mentions ?? []) as DevBoardMention[],
    count: Number.isFinite(count) && count > 0 ? count : 0,
  };
}

/**
 * Unread Dev Board mentions, shared by the sidebar badge and the mention
 * toaster so they cost one request per minute. It keeps polling in a hidden
 * tab because the toaster raises an OS notification there. Acknowledging
 * mentions (DEV_BOARD_MENTIONS_READ_EVENT) refreshes it right away. Accounts
 * without Dev Board access (403) and expired sessions (401) stop the poll.
 */
export function useDevBoardNotifications(enabled: boolean) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return undefined;
    // Both consumers listen; joining the running refetch keeps it to one request.
    const onRead = () => {
      void queryClient.invalidateQueries(
        { queryKey: DEV_BOARD_NOTIFICATIONS_KEY },
        { cancelRefetch: false },
      );
    };
    window.addEventListener(DEV_BOARD_MENTIONS_READ_EVENT, onRead);
    return () => window.removeEventListener(DEV_BOARD_MENTIONS_READ_EVENT, onRead);
  }, [enabled, queryClient]);

  return useQuery({
    queryKey: DEV_BOARD_NOTIFICATIONS_KEY,
    queryFn: fetchDevBoardNotifications,
    enabled,
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => pollingRefetchInterval(query, DEV_BOARD_NOTIFICATIONS_POLL_MS),
    refetchIntervalInBackground: true,
  });
}
